
// date-utils.ts
import { format, startOfDay, parseISO, formatDuration, intervalToDuration } from 'date-fns';

/**
 * Formats a date to ISO format (YYYY-MM-DD) in UTC
 * @param date Date to format
 * @returns Formatted date string
 */
/**
 * Formats a date to ISO format (YYYY-MM-DD) in local timezone
 * @param date Date to format
 * @returns Formatted date string
 */
export const formatDateToISO = (date: Date): string => {
  // Use local date components instead of UTC to maintain consistency
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  const formattedDate = `${year}-${month}-${day}`;
  
  return formattedDate;
};

/**
 * Formats a date for display in the UI
 * @param dateInput Date or ISO string to format
 * @param shortFormat Whether to use short format (defaults to false)
 * @returns Formatted date string for display
 */
export const formatDateForDisplay = (dateInput: Date | string, shortFormat = false): string => {
  const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
  return format(date, shortFormat ? 'EEE, MMM d' : 'EEEE, MMMM d, yyyy');
};

/**
 * Formats time in minutes to HH:MM format
 * @param minutes Number of minutes
 * @returns Formatted time string
 */
export const formatMinutesToHHMM = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Formats a time duration in milliseconds to a human-readable string
 * @param ms Duration in milliseconds
 * @returns Formatted time string
 */
export const formatTime = (ms: number): string => {
  if (isNaN(ms) || ms < 0) {
    return '00:00:00';
  }
  
  // Convert to seconds, minutes, hours
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor(ms / (1000 * 60 * 60));
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
};

/**
 * Formats a timestamp to a readable time
 * @param timestamp ISO timestamp string
 * @returns Formatted time string
 */
export const formatTimestamp = (timestamp: string): string => {
  return format(new Date(timestamp), 'h:mm a');
};

/**
 * Converts UTC date string to local date for display
 * @param utcDate UTC date string in format YYYY-MM-DD
 * @returns Date object in local timezone
 */
export const utcDateToLocal = (utcDate: string): Date => {
  // Create a date object that represents midnight UTC on the given date
  const date = new Date(`${utcDate}T00:00:00Z`);
  return date;
};

/**
 * Converts local date to UTC date string for storage
 * @param localDate Local date object
 * @returns UTC date string in format YYYY-MM-DD
 */
export const localDateToUtc = (localDate: Date): string => {
  return formatDateToISO(localDate);
};
